import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface Meeting {
  id: string;
  deal_id?: string;
  lead_id?: string;
  contact_id?: string;
  title: string;
  description?: string;
  scheduled_at: string;
  location?: string;
  meeting_url?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show' | 'rescheduled';
  completed_at?: string;
  cancelled_at?: string;
  cancelled_reason?: string;
  attendees?: any[];
  created_by?: string;
  google_calendar_event_id?: string;
  google_calendar_html_link?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any> | null;
  transcript?: string | null;
}

export function useMeetings(dealId?: string, leadId?: string, contactId?: string) {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const loadMeetings = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('calls')
        .select('*')
        .eq('channel', 'video')
        .order('scheduled_for', { ascending: false });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      } else if (leadId) {
        query = query.eq('lead_id', leadId);
      } else if (contactId) {
        // For contacts, get meetings from their deals AND directly linked to contact
        const { data: deals } = await supabase
          .from('deals')
          .select('id')
          .eq('contact_id', contactId);
        
        // Build OR condition: meetings with this contact_id OR meetings from their deals
        if (deals && deals.length > 0) {
          query = query.or(`contact_id.eq.${contactId},deal_id.in.(${deals.map(d => d.id).join(',')})`);
        } else {
          // No deals, just get meetings directly linked to this contact
          query = query.eq('contact_id', contactId);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Fetch transcripts from meetings table for video calls that have a meeting_url
      const meetingUrls = (data || [])
        .filter(c => c.meeting_url)
        .map(c => c.meeting_url!);

      let transcriptMap: Record<string, string> = {};
      if (meetingUrls.length > 0) {
        const { data: meetingsData } = await supabase
          .from('meetings')
          .select('daily_room_url, transcript')
          .in('daily_room_url', meetingUrls)
          .not('transcript', 'is', null);

        if (meetingsData) {
          for (const m of meetingsData) {
            if (m.daily_room_url && m.transcript) {
              transcriptMap[m.daily_room_url] = m.transcript;
            }
          }
        }
      }
      
      // Map calls fields to meeting fields for backward compatibility
      const mappedMeetings = (data || []).map(call => ({
        id: call.id,
        deal_id: call.deal_id,
        lead_id: call.lead_id,
        contact_id: call.contact_id,
        title: call.title || '',
        description: call.notes,
        scheduled_at: call.scheduled_for || call.created_at,
        location: (call.metadata as any)?.location,
        meeting_url: call.meeting_url,
        status: call.status as Meeting['status'],
        completed_at: call.completed_at,
        cancelled_at: call.cancelled_at,
        cancelled_reason: call.cancelled_reason,
        attendees: call.attendees || [],
        created_by: call.created_by,
        google_calendar_event_id: call.google_calendar_event_id,
        google_calendar_html_link: call.google_calendar_html_link,
        created_at: call.created_at,
        updated_at: call.updated_at,
        metadata: call.metadata as Record<string, any> | null,
        transcript: call.meeting_url ? transcriptMap[call.meeting_url] || null : null,
      })) as Meeting[];
      
      setMeetings(mappedMeetings);
    } catch (err) {
      setError(err as Error);
      toast({
        variant: 'destructive',
        title: 'Error loading meetings',
        description: (err as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  const createMeeting = async (meetingData: any) => {
    try {
      // Transform meeting data to calls schema
      const callData = {
        channel: (meetingData.meeting_url ? 'video' : 'voice') as 'video' | 'voice',
        direction: 'outbound' as const,
        provider: 'manual',
        contact_id: meetingData.contact_id,
        lead_id: meetingData.lead_id,
        deal_id: meetingData.deal_id,
        title: meetingData.title,
        notes: meetingData.description,
        meeting_url: meetingData.meeting_url,
        scheduled_for: meetingData.scheduled_at,
        status: meetingData.status || 'scheduled',
        attendees: meetingData.attendees || [],
        google_calendar_event_id: meetingData.google_calendar_event_id,
        google_calendar_html_link: meetingData.google_calendar_html_link,
        created_by: meetingData.created_by,
        metadata: {
          location: meetingData.location,
        },
      };

      const { data, error: insertError } = await supabase
        .from('calls')
        .insert(callData)
        .select()
        .single();

      if (insertError) throw insertError;

      await loadMeetings();
      return data;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error creating meeting',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const updateMeeting = async (id: string, updates: any, options?: { skipReload?: boolean; skipToast?: boolean }) => {
    try {
      // Transform meeting updates to calls schema
      const callUpdates: any = {};
      if (updates.title !== undefined) callUpdates.title = updates.title;
      if (updates.description !== undefined) callUpdates.notes = updates.description;
      if (updates.meeting_url !== undefined) callUpdates.meeting_url = updates.meeting_url;
      if (updates.scheduled_at !== undefined) callUpdates.scheduled_for = updates.scheduled_at;
      if (updates.status !== undefined) callUpdates.status = updates.status;
      if (updates.completed_at !== undefined) callUpdates.completed_at = updates.completed_at;
      if (updates.cancelled_at !== undefined) callUpdates.cancelled_at = updates.cancelled_at;
      if (updates.cancelled_reason !== undefined) callUpdates.cancelled_reason = updates.cancelled_reason;
      if (updates.attendees !== undefined) callUpdates.attendees = updates.attendees;
      if (updates.google_calendar_event_id !== undefined) callUpdates.google_calendar_event_id = updates.google_calendar_event_id;
      if (updates.google_calendar_html_link !== undefined) callUpdates.google_calendar_html_link = updates.google_calendar_html_link;
      if (updates.location !== undefined) {
        callUpdates.metadata = { location: updates.location };
      }

      const { error: updateError } = await supabase
        .from('calls')
        .update(callUpdates)
        .eq('id', id);

      if (updateError) throw updateError;

      if (!options?.skipToast) {
        toast({
          title: 'Meeting updated',
          description: 'The meeting has been updated successfully.',
        });
      }

      if (!options?.skipReload) {
        await loadMeetings();
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error updating meeting',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const deleteMeeting = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('calls')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Meeting deleted',
        description: 'The meeting has been deleted successfully.',
      });

      await loadMeetings();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error deleting meeting',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const completeMeeting = async (id: string) => {
    return updateMeeting(id, {
      status: 'completed',
      completed_at: new Date().toISOString(),
    });
  };

  const cancelMeeting = async (id: string, reason: string) => {
    try {
      // 1. Buscar o meeting primeiro para pegar google_calendar_event_id
      const { data: meeting, error: fetchError } = await supabase
        .from('calls')
        .select('google_calendar_event_id, created_by')
        .eq('id', id)
        .eq('channel', 'video')
        .single();

      if (fetchError) throw fetchError;

      // 2. Se tem evento no Google Calendar, tentar deletar
      if (meeting?.google_calendar_event_id && meeting?.created_by) {
        try {
          await supabase.functions.invoke('google-calendar-delete-event', {
            body: {
              host_user_id: meeting.created_by,
              event_id: meeting.google_calendar_event_id,
            },
          });
          
          toast({
            title: 'Reunião cancelada',
            description: 'Removida do Google Calendar de todos os participantes.',
          });
        } catch (calError) {
          console.error('Failed to delete from Google Calendar:', calError);
          toast({
            title: 'Reunião cancelada',
            description: 'Mas não foi possível remover do calendário.',
            variant: 'destructive',
          });
        }
      }

      // 3. Atualizar status no banco
      return updateMeeting(id, {
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancelled_reason: reason,
      });
      
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao cancelar reunião',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  const rescheduleMeeting = async (
    id: string, 
    updates: {
      scheduled_at: string;
      duration_minutes?: number;
      location?: string;
      title?: string;
      description?: string;
      attendees?: string[];
    }
  ) => {
    try {
      // 1. Buscar meeting atual
      const { data: meeting, error: fetchError } = await supabase
        .from('calls')
        .select('google_calendar_event_id, created_by, title, notes, metadata, attendees')
        .eq('id', id)
        .eq('channel', 'video')
        .single();

      if (fetchError) throw fetchError;

      // 2. Atualizar no banco local SEMPRE
      await updateMeeting(id, {
        ...updates,
        status: 'scheduled',
        updated_at: new Date().toISOString(),
      });

      // 3. Verificar se deve tentar sincronizar com Google Calendar
      const shouldSyncWithGoogle = meeting?.google_calendar_event_id && meeting?.created_by;
      
      if (!shouldSyncWithGoogle) {
        // Não tem evento no Google Calendar ou não tem host
        toast({
          title: 'Reunião reagendada',
          description: 'Data e hora atualizadas localmente.',
        });
        return;
      }

      // 4. Verificar se o host tem Google Calendar conectado
      console.log('Checking Google Calendar connection for user:', meeting.created_by);
      
      const { data: tokens, error: tokensError } = await supabase
        .from('google_calendar_tokens')
        .select('id, access_token')
        .eq('app_user_id', meeting.created_by)
        .maybeSingle();

      if (tokensError) {
        console.error('Error checking Google Calendar tokens:', tokensError);
      }

      if (!tokens || !tokens.access_token) {
        // Host não tem Google Calendar conectado
        console.log('Host does not have Google Calendar connected');
        toast({
          title: 'Reunião reagendada',
          description: 'Atualizado localmente. Host não tem Google Calendar conectado.',
        });
        return;
      }

      // 5. Host tem Google Calendar, tentar sincronizar
      console.log('Attempting to sync with Google Calendar...');
      
      try {
        const startDate = new Date(updates.scheduled_at);
        const endDate = new Date(startDate.getTime() + 60 * 60000); // Default 60 minutes

        const { data: result, error: invokeError } = await supabase.functions.invoke('google-calendar-update-event', {
          body: {
            host_user_id: meeting.created_by,
            event_id: meeting.google_calendar_event_id,
            title: updates.title || meeting.title,
            description: updates.description || meeting.notes,
            start_time: startDate.toISOString(),
            end_time: endDate.toISOString(),
            location: updates.location || (meeting.metadata as any)?.location,
            attendees: updates.attendees || meeting.attendees,
          },
        });

        if (invokeError) {
          console.error('Edge function error:', invokeError);
          throw invokeError;
        }
        
        console.log('Google Calendar updated successfully:', result);
        
        toast({
          title: 'Reunião reagendada',
          description: 'Todos os participantes foram notificados via email.',
        });
      } catch (calError) {
        console.error('Failed to update Google Calendar:', calError);
        toast({
          title: 'Reunião reagendada',
          description: 'Atualizado localmente. Não foi possível sincronizar com Google Calendar.',
        });
      }
      
    } catch (err) {
      console.error('Reschedule error:', err);
      toast({
        variant: 'destructive',
        title: 'Erro ao reagendar reunião',
        description: (err as Error).message,
      });
      throw err;
    }
  };

  useEffect(() => {
    loadMeetings();
  }, [dealId, leadId, contactId]);

  return {
    meetings,
    loading,
    error,
    loadMeetings,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    completeMeeting,
    cancelMeeting,
    rescheduleMeeting,
  };
}
